import { BotSequenceEventRow } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";

import { DetailCard, FactRow } from "../SequenceSuppressions/SequenceSuppressions.DetailPanel";
import {
  IdList,
  SEQUENCE_LABELS,
  SuppressionsViewLevel,
  externalIdList,
  formatInstant,
  formatSubject,
  formatUser
} from "../SequenceSuppressions/SequenceSuppressions.Shared";
import { DeliveriesCell, SUPPRESSION_LINKED_STATUSES, StatusChip, describeMissedReason, statusChipLabel } from "./SequenceEvents.Shared";

interface SequenceEventDetailPanelProps {
  event: BotSequenceEventRow;
  viewLevel: SuppressionsViewLevel;
  /** Narrows this page to the user's not-sent events (the former Suppressions page, absorbed — ENG-3811). */
  onViewSuppressions: (userId: number) => void;
}

/**
 * The right-side detail of one clicked event (the CRM interaction model, same card
 * language as the suppressions panel). The agent level shows the human-readable facts;
 * the admin level additionally reveals internal / FMS ids and the engine's recorded
 * actions (timeline, raw status, re-dispatches). A not-sent event links onward to the
 * Sequence Suppressions page, where its outcome/reason taxonomy lives.
 */
export function SequenceEventDetailPanel({ event, viewLevel, onViewSuppressions }: SequenceEventDetailPanelProps): React.ReactElement {
  const isAdmin = viewLevel === "admin";
  const timeline = Object.entries(event.timeline);
  const isSuppressionLinked = SUPPRESSION_LINKED_STATUSES.has(event.status);

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-grey-50">
      {/* No close button here — the shared Panel renders its own top-right X (onToggle). */}
      <div className="sticky top-0 z-20 flex items-center bg-grey-50 px-3 py-3">
        <p className="font-poppins font-semibold">Sequence event</p>
      </div>

      <div className="flex flex-1 flex-col gap-3 px-3 pb-9">
        <DetailCard title="Event">
          <FactRow
            label="Status"
            value={
              <span className="flex items-center gap-2">
                <StatusChip status={event.status} reason={event.reason} />
                {describeMissedReason(event.reason, event.missedOffsets, event.stepOffset)}
              </span>
            }
          />
          <FactRow label="Sequence" value={`${SEQUENCE_LABELS[event.sequenceType]}, day ${event.stepOffset}`} />
          {/* localDay is the location's own business day — shown verbatim, never converted. */}
          <FactRow label="Day" value={<span className="font-mono">{event.localDay}</span>} />
          <FactRow label="Time" value={formatInstant(event.occurredAt)} />
          <FactRow label="Attempted" value={<DeliveriesCell deliveries={event.deliveries} />} />
        </DetailCard>

        <DetailCard title="Customer">
          <FactRow label="User" value={formatUser(event.userName, event.userId)} />
          <FactRow label="Location" value={event.locationName ?? "—"} />
          <FactRow label="Subject" value={formatSubject(event.leaseId, event.leadId)} />
        </DetailCard>

        {event.errorDetail && (
          <DetailCard title="Error">
            {/* Full text only here — the table row never truncates an error, it flags one. */}
            <pre className="rounded-md bg-red-50 p-2 font-mono text-xs whitespace-pre-wrap text-red-900 select-all">{event.errorDetail}</pre>
          </DetailCard>
        )}

        {isAdmin && (
          <>
            <DetailCard title="Ids (admin)">
              <IdList
                ids={[
                  { label: "Attempt", value: `#${event.attemptId}` },
                  { label: "Enrollment", value: `#${event.enrollmentId}` },
                  { label: "Org", value: `#${event.orgId}` },
                  { label: "Location", value: event.locationId === null ? null : `#${event.locationId}` },
                  { label: "User", value: `#${event.userId}` },
                  { label: "Tenant", value: event.tenantId === null ? null : `#${event.tenantId}` }
                ]}
              />
              <IdList ids={externalIdList(event.externalIds)} />
            </DetailCard>

            <DetailCard title="Engine actions (admin)">
              {timeline.length === 0 ? (
                <span className="text-sm text-grey-500">No transition stamps recorded.</span>
              ) : (
                <div className="flex flex-col gap-1 text-sm">
                  {timeline.map(([stage, at]) => (
                    <span key={stage} className="text-grey-700">
                      {stage} <span className="font-mono">{formatInstant(at)}</span>
                    </span>
                  ))}
                </div>
              )}
              {event.redispatchCount !== null && event.redispatchCount > 0 && (
                <span className="text-sm text-grey-600">Re-dispatched {event.redispatchCount}×</span>
              )}
              <IdList
                ids={[
                  { label: "Anchor day (location-local)", value: event.anchorDate },
                  { label: "Raw status", value: event.status },
                  { label: "Status", value: statusChipLabel(event.status, event.reason) }
                ]}
              />
            </DetailCard>
          </>
        )}

        {isSuppressionLinked && (
          <div>
            <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => onViewSuppressions(event.userId)}>
              View this user&apos;s not-sent events →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
